import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  dismissAiProcessingItem,
  fetchAiProcessingStats,
  fetchAiProcessingSummary,
} from '@/api/ai';
import type { AiProcessingItem } from '@/api/ai/types';
import {
  cancelAuditAiWorkflow,
  fetchCapaWorkflow,
  triggerAuditAiWorkflow,
} from '@/api/audits';
import type { CapImportContext } from '@/api/audits/types';
import { cancelCertificateAiWorkflow } from '@/api/certifications';
import { useTranslator } from '@/hooks/useTranslator';

const SUMMARY_KEY = ['ai-processing-summary'];
const STATS_KEY = ['ai-processing-stats'];

/**
 * Data + mutations for the navbar AI-processing widget / sheet. Two independent
 * 10s-interval queries (summary + stats) that self-disable when nothing is
 * processing, plus dismiss / cancel / retry mutations. Query keys are shared
 * with the rest of the app (`useProcessingStatsSync`, the agentic Processing
 * tab), so React Query dedupes the fetches and every write invalidation keeps
 * all surfaces in sync.
 */
export function useAiProcessingData() {
  const queryClient = useQueryClient();
  const { getTranslation } = useTranslator({ page: 'aiProcessing' });

  const statsQuery = useQuery({
    queryKey: STATS_KEY,
    queryFn: fetchAiProcessingStats,
    refetchInterval: query => {
      const stats = query.state.data;
      return stats && stats.processing > 0 ? 10_000 : false;
    },
  });

  const summaryQuery = useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: () => fetchAiProcessingSummary(),
    refetchInterval: query => {
      const summary = query.state.data;
      return summary?.items.some(i => i.status === 'PROCESSING')
        ? 10_000
        : false;
    },
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
    void queryClient.invalidateQueries({ queryKey: STATS_KEY });
  }

  const dismissMutation = useMutation({
    mutationFn: dismissAiProcessingItem,
  });

  // Cancel the running workflow for the asset type, then dismiss the row. The
  // cancel is best-effort — a failed cancel must not block the dismiss.
  async function cancelAndDismissItem(item: AiProcessingItem) {
    if (item.assetType === 'AUDIT') {
      try {
        await cancelAuditAiWorkflow(item.assetId);
      } catch {
        /* ignore — dismiss regardless */
      }
    } else if (item.assetType === 'CERTIFICATE') {
      try {
        await cancelCertificateAiWorkflow(item.assetId);
      } catch {
        /* ignore — dismiss regardless */
      }
    }
    await dismissMutation.mutateAsync(item.id);
    invalidate();
  }

  // Retry a failed AUDIT workflow: re-run `triggerAuditAiWorkflow` with the
  // failed workflow's file ids, then dismiss the stale failed row so it isn't
  // duplicated. Audit-only — other asset types have no retry today.
  const retryMutation = useMutation({
    mutationFn: async (item: AiProcessingItem) => {
      // `CapaWorkflow.metadata` is an untyped JSONB bag on the wire — narrow the
      // two fields the retry needs (the file ids live outside `metadata`).
      const workflow = (await fetchCapaWorkflow(item.assetId)) as {
        auditFileId?: string | null;
        capaFileId?: string | null;
        metadata?: { importContext?: CapImportContext } | null;
      };
      const meta = item.metadata as Record<string, unknown> | null;
      const auditFileId =
        workflow.auditFileId ??
        (meta?.auditFileId as string | undefined) ??
        (meta?.fileId as string | undefined);
      const capFileId =
        workflow.capaFileId ??
        (meta?.capFileId as string | undefined) ??
        (meta?.capaFileId as string | undefined);
      const body: {
        auditFileId?: string;
        capFileId?: string;
        importContext?: CapImportContext;
      } = {};
      if (auditFileId) body.auditFileId = auditFileId;
      if (capFileId) body.capFileId = capFileId;
      // Carry the CAP-import context onto the fresh workflow, or a retried
      // CAP-only import loses the audit/company/plan it was started for and its
      // new queue card can no longer be resumed into a review.
      const importContext = workflow.metadata?.importContext;
      if (importContext) body.importContext = importContext;
      await triggerAuditAiWorkflow(body);
      await dismissAiProcessingItem(item.id);
    },
  });

  return {
    stats: statsQuery.data ?? null,
    items: summaryQuery.data?.items ?? [],
    isLoading: summaryQuery.isLoading,
    dismissItem: async (id: string) => {
      await dismissMutation.mutateAsync(id);
      invalidate();
    },
    cancelAndDismissItem,
    // Callers fire this from a click handler (`void onRetry(item)`), so it must
    // never reject: a rejection there becomes an unhandled promise rejection and
    // the user gets no feedback. Both outcomes are reported by a toast instead.
    retryItem: async (item: AiProcessingItem) => {
      try {
        await retryMutation.mutateAsync(item);
        toast.success(getTranslation('stage.retryStarted'));
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : getTranslation('stage.retryFailed'),
        );
      }
      invalidate();
    },
    isRetrying: retryMutation.isPending,
    invalidate,
  };
}
